import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { useDashboardStore } from 'store/dashboard.store';

import { useMutationCreateDashboard } from 'api/mutations/createDashboard.mutation';
import { useMutationSetDashboardById } from 'api/mutations/setDashboardById.mutation';
import { useQueryGetDashboards } from 'api/queries/getDashboards.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import classes from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget.module.scss';
import { ListWrapper } from 'components/Dashboards/Project/Components/CustomDashboard/ListWrapper/listWrapper';
import { addWidgetToDashboard } from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import {
    IComponentKey,
    IDashboard,
    IFilter,
    ILocalFilters,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { IVersionCompareTableConfig } from 'components/Dashboards/Project/Components/VersionCompare/VersionCompare.consts';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';

export interface Props {
    componentKey: IComponentKey;
    title: string;
    projectId?: number | null;
    contractId?: number | null;
    widgetId?: string;
    elementId?: keyof IVersionCompareTableConfig;
    route: string;
    filters?: IFilter[];
    localFilters?: ILocalFilters;
    callBack?: (widgetId: string) => void;
    shouldUseNewIcon?: boolean;
    shouldHide?: boolean;
}

export const AddWidget = ({
    componentKey,
    title,
    projectId,
    contractId,
    widgetId,
    elementId,
    route,
    filters,
    localFilters,
    callBack,
    shouldUseNewIcon,
    shouldHide,
}: Props) => {
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [dropdownAnchor, setDropdownAnchor] = useState(null);
    const [modalManagerOpen, setModalManagerOpen] = useState<boolean>(false);
    const { setSelectedDashboard } = useDashboardStore();
    const { handleSnackBar } = useCustomSnackBar();
    const { data, refetch } = useQueryGetDashboards();
    const dashboards: IDashboard[] = data?.dashboardDetails || [];
    const { mutate: mutateCreateDashboard } = useMutationCreateDashboard();
    const { mutate: mutateSetDashboardById } = useMutationSetDashboardById();

    const { ability } = useUserHook();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleDropdownOpen = (event) => {
        setDropdownAnchor(event.currentTarget);
        setDropdownOpen((isOpen) => !isOpen);
    };

    const handleAddWidgetToDashboard = ({
        dashboardId,
        dashboards,
    }: {
        dashboardId: number | undefined;
        dashboards: IDashboard[] | undefined;
    }) => {
        if (!dashboards) return null;
        const dashboard = dashboards?.find((item) => item.id === dashboardId);
        const updatedDashboard = addWidgetToDashboard({
            componentKey,
            title,
            projectId,
            contractId,
            elementId,
            dashboard,
            route,
            filters,
            localFilters,
        });

        dashboard &&
            mutateSetDashboardById(
                { dashboard: updatedDashboard },
                {
                    onSuccess: (response) => {
                        const newWidgets = response ? response.widgets : [];
                        const widgetId = newWidgets[newWidgets.length - 1].id;
                        callBack && widgetId && callBack(widgetId);
                        setDropdownOpen(false);
                        handleSnackBar(
                            <>
                                Component successfully added to Pinboard{' '}
                                <Link
                                    to={AppRoutes.buildPath(
                                        AppRoutes.DASHBOARD_EXECUTION_PATH,
                                        executionConfig.pinboards.link,
                                        dashboard.id,
                                    )}
                                    className={classes.link}
                                >
                                    {dashboard.name}
                                </Link>
                            </>,
                            'success',
                        );

                        setSelectedDashboard({
                            dashboard: dashboardId,
                        });
                        sendEventWithDimensions({
                            category: 'Pinboard',
                            action: 'Add To Pinboard',
                            label: dashboard?.name,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                    onSettled: () => refetch(),
                },
            );
    };
    const handleCreateDashboard = (values) => {
        const name = values.dashboard;
        mutateCreateDashboard(
            { name },
            {
                onSuccess: (response) => {
                    const dashboardId = response.id;
                    setModalManagerOpen(false);
                    refetch().then((response) => {
                        handleSnackBar('Dashboard created successfully', 'success');
                        handleAddWidgetToDashboard({ dashboardId, dashboards: response?.data?.dashboardDetails });
                        sendEventWithDimensions({
                            category: 'Pinboard',
                            action: 'Create Pinboard',
                            label: name,
                        });
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    if (!ability.can('update', 'PinboardsEdit') || widgetId || shouldHide) {
        return null;
    }

    return (
        <>
            <CustomizedTooltip
                tooltipPosition={TooltipPosition.Top}
                tooltipContent={`Add to Custom Pinboard`}
                triggerElement={
                    shouldUseNewIcon ? (
                        <Button variant={'tertiary'} size={'small'} onClick={handleDropdownOpen}>
                            <MuiIcon
                                fontSize={'25px'}
                                icon={'dashboard_customize_outlined'}
                                color={'var(--button-tertiary-fg)'}
                            />
                        </Button>
                    ) : (
                        <CustomizedRoundIcon onClick={handleDropdownOpen}>
                            <Icon name={'add-widget'} />
                        </CustomizedRoundIcon>
                    )
                }
            />

            <ListWrapper
                title={'Add To:'}
                dropdownAnchor={dropdownAnchor}
                setDropdownOpen={setDropdownOpen}
                dropdownOpen={dropdownOpen}
                handleCreateDashboard={handleCreateDashboard}
                dashboards={dashboards}
                handleClick={handleAddWidgetToDashboard}
                modalManagerOpen={modalManagerOpen}
                setModalManagerOpen={setModalManagerOpen}
            />
        </>
    );
};
